interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        ShowX obsahuje <strong>MCP server</strong> (stdio transport, 15 nástrojů), který umožňuje AI agentovi
        — například Claude — řídit show programaticky. Agent může sestavit celý show z textového scénáře,
        pohybovat playheadem, přidávat a editovat cues — vše bez ruční práce v UI.
      </p>
      <p>
        MCP ovládání funguje <strong>výhradně v REHEARSAL módu</strong>. V SHOW módu jsou write operace
        blokovány; agent má přístup pouze ke čtení (get_state, list_cuelists, get_cuelist, …).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Indikátor agenta v UI</h3>
      <p>
        Když je MCP server připojen a aktivní, shell zobrazí{' '}
        <strong>indikátor "agent"</strong> v patičce hlavního okna. Indikátor zhasne, když se agent odpojí
        nebo ShowX ztratí spojení s control hubem (port 9441).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Připojení — Claude Code (auto-discovery)</h3>
      <p>
        ShowX repo obsahuje soubor <code>.mcp.json</code> v kořeni, který Claude Code auto-detekuje. Spustit
        MCP server:
      </p>
      <ol>
        <li>Buildni MCP balík: <code>pnpm install && pnpm --filter @showx/mcp build</code></li>
        <li>Restartuj Claude Code v kořeni ShowX repo.</li>
        <li>MCP server se zaregistruje automaticky.</li>
        <li>Spusť ShowX (<code>pnpm dev:electron</code> nebo instalovaný DMG) — shell se připojí k hubu.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Připojení — Claude Desktop</h3>
      <p>
        Přidej do <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>:
      </p>
      <pre className="bg-surface-2 rounded p-4 text-sm overflow-x-auto my-4">{`{
  "mcpServers": {
    "showx": {
      "command": "node",
      "args": ["/absolute/cesta/k/showX/mcp/dist/index.js"]
    }
  }
}`}</pre>
      <p>
        Absolutní cesta musí odpovídat tvé instalaci. Po restartu Claude Desktop je server dostupný.
        ShowX musí být spuštěn (port 9441).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">15 nástrojů (přehled)</h3>
      <ul>
        <li><code>get_show_summary</code> — metadata show, mód, aktivní cuelist</li>
        <li><code>list_cuelists</code> — všechny cuelisty (id, jméno, počet cues)</li>
        <li><code>get_cuelist</code> — plný cuelist s cues a souhrny payloadů</li>
        <li><code>get_state</code> — stav přehrávání (mód, playhead)</li>
        <li><code>create_cuelist</code> — vytvoří nový prázdný cuelist</li>
        <li><code>add_cue</code> — připojí cue na konec cuelistu</li>
        <li><code>update_cue</code> — opraví pole cue (label, trigger, číslo cue, …)</li>
        <li><code>set_cue_payloads</code> — nahradí všechny payloady na cue</li>
        <li><code>reorder_cues</code> — přeřadí cues podle pole id</li>
        <li><code>delete_cue</code> — smaže cue (vyžaduje <code>confirm: true</code>)</li>
        <li><code>set_meta</code> — aktualizuje název show / místo / datum</li>
        <li><code>transport_go</code> — spustí cue</li>
        <li><code>transport_standby</code> — pošle žádost standby</li>
        <li><code>transport_jump</code> — přesune playhead bez spuštění</li>
        <li><code>undo_last_batch</code> — odvolá veškeré zápisy z posledního batche agenta</li>
      </ul>
      <p>
        Resource <code>showx://guide</code> — markdown referenční průvodce: mentální model, payload cheatsheet,
        trigger tvary, pravidlo SHOW-mode read-only, krok-za-krokem workflow sestavení show.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Příklad promptu (CZ)</h3>
      <p>
        Agent je schopen sestavit celou show z textového podkladu. Příklad:
      </p>
      <pre className="bg-surface-2 rounded p-4 text-sm overflow-x-auto my-4 whitespace-pre-wrap">{`Postav show pro firemní večírek z tohoto programu:
18:00 Uvítání — řeč CEO, LX plný, fanfára na QLab
18:15 Prezentace výsledků — projekce slideshow, ztlumit světla na 40 %
18:45 Tombola — playback video na QLab, pulse světel
19:00 Raut — světla na 60 %, hudba fade in přes OSC do QLab

Vytvoř cuelist "Firemní večírek", přidej cues s OSC payloady pro QLab
(device_id: dev_qlab, adresa /cue/<jméno>/start) a LX ref payloady
pro Eos (device_id: dev_eos, cue_list: 1).`}</pre>
      <p>
        Agent přečte <code>showx://guide</code>, vytvoří cuelist, přidá cues a payloady. Výsledek
        zkontroluj v ShowX UI a podle potřeby doupravuj v REHEARSAL módu.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">SHOW mód — agent read-only</h3>
      <p>
        V SHOW módu agent nemůže psát. Volání{' '}
        <code>add_cue</code>, <code>update_cue</code>, <code>set_cue_payloads</code>, <code>delete_cue</code>,
        {' '}<code>create_cuelist</code>, <code>set_meta</code> vrátí chybu{' '}
        <em>"read-only-in-show"</em>. <code>transport_go</code> je v SHOW módu také blokované.
        Pouze <code>transport_standby</code> a <code>transport_jump</code> jsou v SHOW módu povoleny —
        slouží k navigaci playheadu bez spouštění výstupů.
      </p>
      <p>
        Tato ochrana zabraňuje nechtěné mutaci show struktury za živé produkce.
      </p>
      <p className="mt-8 text-sm text-muted">
        Viz také: <a href="/docs" className="text-accent-deep underline underline-offset-2">Dokumentace ShowX</a> — architektura, specy, protocol reference.
      </p>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        ShowX includes an <strong>MCP server</strong> (stdio transport, 15 tools) that lets an AI agent —
        such as Claude — control the show programmatically. An agent can build an entire show from a text
        script, move the playhead, add and edit cues — all without manual UI work.
      </p>
      <p>
        MCP control works <strong>in REHEARSAL mode only</strong>. In SHOW mode, write operations are blocked;
        the agent has read-only access (get_state, list_cuelists, get_cuelist, …).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Agent indicator in the UI</h3>
      <p>
        When the MCP server is connected and active, the shell shows an{' '}
        <strong>"agent" indicator</strong> in the main window footer. The indicator goes off when the agent
        disconnects or ShowX loses its connection to the control hub (port 9441).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Connect — Claude Code (auto-discovery)</h3>
      <p>
        The ShowX repo includes a <code>.mcp.json</code> file at the root, which Claude Code auto-detects.
        To start the MCP server:
      </p>
      <ol>
        <li>Build the MCP package: <code>pnpm install && pnpm --filter @showx/mcp build</code></li>
        <li>Restart Claude Code in the ShowX repo root.</li>
        <li>The MCP server registers automatically.</li>
        <li>Start ShowX (<code>pnpm dev:electron</code> or the installed DMG) — the shell connects to the hub.</li>
      </ol>

      <h3 className="display-serif text-xl mt-10 mb-3">Connect — Claude Desktop</h3>
      <p>
        Add to <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>:
      </p>
      <pre className="bg-surface-2 rounded p-4 text-sm overflow-x-auto my-4">{`{
  "mcpServers": {
    "showx": {
      "command": "node",
      "args": ["/absolute/path/to/showX/mcp/dist/index.js"]
    }
  }
}`}</pre>
      <p>
        The absolute path must match your installation. After restarting Claude Desktop, the server is
        available. ShowX must be running (port 9441).
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">15 tools (overview)</h3>
      <ul>
        <li><code>get_show_summary</code> — show metadata, mode, active cuelist</li>
        <li><code>list_cuelists</code> — all cuelists (id, name, cue count)</li>
        <li><code>get_cuelist</code> — full cuelist with cues and payload summaries</li>
        <li><code>get_state</code> — playback state (mode, playhead)</li>
        <li><code>create_cuelist</code> — create a new empty cuelist</li>
        <li><code>add_cue</code> — append a cue to a cuelist</li>
        <li><code>update_cue</code> — patch cue fields (label, trigger, cue number, …)</li>
        <li><code>set_cue_payloads</code> — replace all payloads on a cue</li>
        <li><code>reorder_cues</code> — reorder cues by id array</li>
        <li><code>delete_cue</code> — delete a cue (requires <code>confirm: true</code>)</li>
        <li><code>set_meta</code> — update show title / venue / date</li>
        <li><code>transport_go</code> — fire a cue</li>
        <li><code>transport_standby</code> — send standby request</li>
        <li><code>transport_jump</code> — move playhead without firing</li>
        <li><code>undo_last_batch</code> — undo all writes from the last agent batch</li>
      </ul>
      <p>
        Resource <code>showx://guide</code> — markdown reference: mental model, payload cheatsheet, trigger
        shapes, SHOW-mode read-only rule, step-by-step show authoring workflow.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Sample prompt (EN)</h3>
      <p>An agent can build an entire show from a text brief. Example:</p>
      <pre className="bg-surface-2 rounded p-4 text-sm overflow-x-auto my-4 whitespace-pre-wrap">{`Build a show for a corporate dinner from this programme:
18:00 Welcome — CEO speech, full LX, fanfare on QLab
18:15 Results presentation — projection slideshow, dim lights to 40%
18:45 Tombola — video playback on QLab, pulse lights
19:00 Reception — lights at 60%, music fade-in via OSC to QLab

Create cuelist "Corporate Dinner", add cues with OSC payloads for QLab
(device_id: dev_qlab, address /cue/<name>/start) and LX ref payloads
for Eos (device_id: dev_eos, cue_list: 1).`}</pre>
      <p>
        The agent reads <code>showx://guide</code>, creates the cuelist, adds cues and payloads. Review the
        result in ShowX UI and fine-tune in REHEARSAL mode.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">SHOW mode — agent read-only</h3>
      <p>
        In SHOW mode the agent cannot write. Calls to{' '}
        <code>add_cue</code>, <code>update_cue</code>, <code>set_cue_payloads</code>, <code>delete_cue</code>,
        {' '}<code>create_cuelist</code>, <code>set_meta</code> return an error:{' '}
        <em>"read-only-in-show"</em>. <code>transport_go</code> is also blocked in SHOW mode.
        Only <code>transport_standby</code> and <code>transport_jump</code> are allowed in SHOW mode —
        for playhead navigation without firing outputs.
      </p>
      <p>
        This protection prevents unintended mutation of the show structure during a live production.
      </p>
      <p className="mt-8 text-sm text-muted">
        See also: <a href="/docs" className="text-accent-deep underline underline-offset-2">ShowX Documentation</a> — architecture, specs, protocol reference.
      </p>
    </>
  )
}

export default function Chapter8({ cs }: Props) {
  return (
    <article id="kap-8" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '8. Ovládání agentem (MCP)' : '8. Agent control (MCP)'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
